import { dublinCivilDate } from './dublin-date.js';
import { portalRequestLead, isPortalRequestPast } from './portal-lead.js';
import { isSettleableRow, type SettleReason } from './portal-request-settle.js';
import { isBookingCancellable } from './status-schema.js';
import { civilDateLabel } from './civil-date-label.js';

/**
 * ⭐⭐ Board 2812 M1 (plan step 3, R-2812-c) - BOARD 2814 M2's `needsHelenNow`,
 * MOVED HERE FROM `client/lib/actions.ts` WITH ITS PURE CLOSURE, behaviour
 * unchanged, so the SERVER can count a home's requests by the same rule the rail
 * badge counts them by (`/api/home-groups`' `needYouNow`, and Tomorrow's
 * `waiting` rows on `/api/bookings`). `actions.ts` re-exports every moved name,
 * so 2814's code and specs read the same functions they always did.
 *
 * ⛔ TYPED BY SHAPE, NEVER BY `PortalRequest` OR `PortalRequestSummary` (the
 * `portal-lead.ts` house rule): the client's type and the server's are separate
 * declarations of the same seam, and `src/shared` is imported by both bundles, so
 * it may depend on neither.
 */

/** The request fields the placement reads. Both `PortalRequest` (client) and
 *  `PortalRequestSummary` (server) satisfy it. */
export type HelenRequest = {
  status: string;
  askState?: string | null | undefined;
  askKind?: string | null | undefined;
  history?: ReadonlyArray<{ kind: string; by: string; at: string }> | null | undefined;
  revision?: number | null | undefined;
  timeMeaning: string | null | undefined;
  pickupAt?: string | null | undefined;
  journeyDate: string | null | undefined;
  schedulePattern?: unknown;
};

/** The crossing fields the placement reads (`PortalCrossing` on the client,
 *  `PortalCrossingSummary` on the server). */
export type HelenCrossing = {
  bookingId: number | null;
  status: string | null;
  jobRef: string | null;
  bookingJourneyDate: string | null;
  bookingJourneyTime: string | null;
  dateChanged: boolean;
  travelled: boolean;
};

/** The settlement field the placement reads (`PortalSettlement` on the client,
 *  `PortalRequestSettlement` on the server). */
export type HelenSettlement = { reason: SettleReason };

/**
 * ⭐ Board 2814 M2 (plan-alignment round 2, N1) - THE RUN ON THIS CROSSING CAN BE
 * ENDED FROM THE REQUESTS SCREEN. ONE predicate, read by `needsHelenNow`'s row 12
 * AND by the row's own `End the run` control, so the list a request sits in and
 * the control it offers cannot disagree.
 * ⭐ Board 2814 M4 (Captain ruling (c), 2026-10-06): the travelled half is THE
 * RULING, and the server enforces it: a travelled run stays on the month's bill
 * from every Requests-screen control (`cancelBooking`'s `refuseTravelled`, which
 * leg B always sets and `POST /api/bookings/:id/cancel` sets unless booking
 * detail's acknowledged confirm sent `travelledAcknowledged`). Booking detail's
 * acknowledged cancel is the only correction path; this row never offers it.
 */
export function canEndRunHere(c: Pick<HelenCrossing, 'status' | 'travelled'>): boolean {
  return isBookingCancellable(c.status) && c.travelled !== true;
}

/** `HH:MM` in Europe/Dublin, 24-hour (`hourCycle: 'h23'`: `hour12: false` alone
 *  renders midnight as 24:00 on some ICU builds), the shape the crossing writes
 *  into `bookings.journey_time`. */
const DUBLIN_CLOCK = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Dublin',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * ⭐ Board 2788 M6 (DD-17) - THE DAY AND TIME A DATED REQUEST'S BOOKING LEADS WITH,
 * by the crossing's own lead rule (`shared/portal-lead.ts`): the derived collection
 * of an appointment, the request's own time of a collection, and the day with no
 * time otherwise. The same answer the server's `crossingJourneyDate` and
 * `crossingJourneyTime` give a dated request; null for a repeating one (no date,
 * so nothing DD-17 compares) or an unreadable instant.
 */
export function requestLeadDayTime(
  r: Pick<HelenRequest, 'timeMeaning' | 'pickupAt' | 'journeyDate' | 'schedulePattern'>,
): { day: string; time: string | null } | null {
  if (r.schedulePattern != null || typeof r.journeyDate !== 'string') return null;
  const lead = portalRequestLead(r);
  const at = lead.kind === 'pickup' ? lead.at : new Date(r.journeyDate);
  if (Number.isNaN(at.getTime())) return null;
  return { day: dublinCivilDate(at), time: lead.kind === 'none' ? null : DUBLIN_CLOCK.format(at) };
}

/** ⭐ Board 2788 M6 (step 3) - an instant as the Dublin day and `HH:MM` a date and a
 *  time input take, or null. Booking detail prefills the linked change with it. */
export function dublinWallClockOf(iso: string | null | undefined): { day: string; time: string } | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : { day: dublinCivilDate(at), time: DUBLIN_CLOCK.format(at) };
}

/**
 * ⭐⭐ Board 2788 M6 (DD-17) - "THE BOOKING SAYS X BUT THE HOME'S REQUEST SAYS Y", or
 * null. Both conditions, exactly as the server's `settledBookingDisagrees`:
 *   · the booking has a `date_changed` event, OR the request's revision is above 0
 *     (a pre-board-2637 crossing wrote the appointment rather than the pickup, so
 *     its "disagreement" is history and is never marked);
 *   · the booking's day and time differ from the request's lead.
 * The two sides come back as words ("Tue, 10 Nov, 09:00"), never raw values.
 */
export function bookingRequestMismatch(
  r: HelenRequest,
  c: HelenCrossing | undefined,
): { booking: string; request: string } | null {
  if (c === undefined || c.bookingId === null) return null;
  const moved = (typeof r.revision === 'number' && r.revision > 0) || c.dateChanged === true;
  if (!moved) return null;
  const want = requestLeadDayTime(r);
  if (want === null) return null;
  if (c.bookingJourneyDate === want.day && (c.bookingJourneyTime ?? null) === want.time) return null;
  const words = (day: string | null, time: string | null): string =>
    day === null ? (time ?? 'no date') : (civilDateLabel(day, time) || `${day} ${time ?? ''}`.trim());
  return { booking: words(c.bookingJourneyDate, c.bookingJourneyTime), request: words(want.day, want.time) };
}

// ---------------------------------------------------------------------------
// ⭐⭐ Board 2814 M2 (plan DD-11) - WHERE EVERY REQUEST LIVES: Needs you now, Done,
// or not listed at all (it is a booking).
// ---------------------------------------------------------------------------

/** ⭐ T1746 M3 (item 2a) - the one sentence a row says when the kiosk could not
 *  read its own crossings. Moved here from the Requests screen at board 2814 M2,
 *  because `needsHelenNow` words that row too. It claims nothing about the row. */
export const CROSSINGS_UNKNOWN_LINE = 'Booking link unavailable. Reload to try again.';

/** ⭐ Board 2814 M1 (DD-4) - a priced or confirmed request whose day passed with no
 *  live booking (DD-11 row 4). The crossing's own refusal says the same sentence. */
export const PAST_LINE = 'This trip’s date has passed. It was not booked here.';

/** ⭐ Board 2814 M2 (DD-11 row 2) - a New request whose day passed unpriced. */
export const NO_ANSWER_PAST_LINE = 'The date passed without an answer.';

/* ⚠️ Board 2814 M4 - `CASH_BY_HAND_LINE` (DD-11 row 8c, the cash interim) STOOD
   HERE AND IS RETIRED with the interim: cash crosses now, so a cash Confirmed row
   with no booking is row 8, in Needs you now, like any other. */

/** One DD-11 table row, by its number in the plan, plus the two states outside it. */
export type HelenRow =
  | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | '13' | '14'
  | 'link-unknown' | 'other';

/**
 * The row's named primary (DD-11's Primary column). `confirm` is the one press
 * that books and confirms (the crossing route), for every payer since board 2814
 * M4 retired the cash interim's status-route Confirm.
 */
export type HelenPrimary = 'save-price' | 'confirm' | 'accept' | 'open-booking' | 'end-the-run';

export type HelenPlace = {
  /** `booking` is DD-11 row 11: not listed on the Requests screen at all. */
  place: 'needs' | 'done' | 'booking';
  row: HelenRow;
  primary: HelenPrimary | null;
  /** Whether that primary is the row's gold. Rows 6 and 12 never are (destructive,
   *  DESIGN.md §5.1), and neither is a cancel ask's Accept, which ends the trip. */
  gold: boolean;
  /** The row's own words, or null when the row's existing lines say it (row 7's
   *  landed line and row 10's DD-17 mismatch line render where 2788 built them). */
  line: string | null;
};

const place = (
  where: HelenPlace['place'],
  row: HelenRow,
  primary: HelenPrimary | null = null,
  line: string | null = null,
): HelenPlace => ({ place: where, row, primary, gold: primary !== null && primary !== 'end-the-run', line });

/**
 * ⭐⭐ Board 2814 M2 (plan DD-11, M2 step 2) - ONE PURE FUNCTION, ONE COMPLETE
 * TABLE: the ONE place a request lives, and what its row renders. The count, the
 * rail badge, the `Requests · N` tab label, the list split, the order and the
 * default-open row all read it, so the count always equals the Needs you now
 * length (2812 R-10). ⭐ Board 2812 M1: and the server's per-home counts
 * (`/api/home-groups`) and Tomorrow's waiting rows read it too, on the server's
 * own clock.
 *
 * ⛔ FIRST MATCH, IN THE ORDER 12, 9, 10, 6, 4, THEN 14, THEN THE REST
 * (plan-review Low 7; board 2814 M4 adds 14 after 4):
 *   12  declined or cancelled, linked booking live and NOT travelled -> Needs, End
 *       the run (no gold). ⚠️ "Live" is `canEndRunHere`, the predicate End the
 *       run itself reads: `isBookingCancellable` AND no `travelled` event on the
 *       booking (travel is an event, never a status: nothing writes `completed`).
 *       ⭐ Board 2814 M4 (Captain ruling (c)): a travelled run stays on the month's
 *       bill from every Requests-screen control, and the server refuses all three,
 *       so it falls to 13 and its row offers no End the run. Booking detail's
 *       acknowledged cancel is the only correction path.
 *   13  declined or cancelled, otherwise -> Done.
 *    9  an OPEN ask on a New, Priced or Confirmed request -> Needs, Accept (gold on
 *       a change ask; a cancel ask's accept is destructive). An open ask on a past
 *       day stays here until it is answered (2788 DD-8).
 *  1/2  New: not past -> Needs, Save price; past -> Done, "The date passed without
 *       an answer".
 *   10  Confirmed, live booking, DD-17 mismatch -> Needs, Open the booking.
 *    6  Priced or Confirmed, booking cancelled -> Needs, the words, no primary.
 *    4  Priced or Confirmed, no live booking, past -> Done, the past line.
 *   14  settled by hand, and the row would otherwise be 7 or 8 (`isSettleableRow`,
 *       the predicate the settle route re-derives) -> Done, the settle words; the
 *       row offers Undo. A settlement on any other row is ignored here (the row
 *       has moved on: it booked, passed, got an ask or was ended).
 *    5  Priced, live booking (the mark failed) -> Needs, Confirm.
 *   11  Confirmed, live booking -> a booking, not listed.
 *    7  landed with no booking -> Needs, Confirm (its landed line says why).
 *  3/8  no crossing: Priced -> 3, Confirmed -> 8; Needs, Confirm.
 *
 * ⛔ LINK UNKNOWN (`linkKnown` false: the crossings map is `null`, or, from board
 * 2814 M4, the settlements map is). Every row whose place depends on the link
 * stays in Needs you now with the neutral line and NO gold; New rows and open asks
 * do not depend on it. (Cash rows did, until M4: cash crosses now, so they do.)
 * "Past" is `isPortalRequestPast`, the crossing's own day rule; a "live" booking
 * is a non-cancelled one. `settlement` is the request's hand settlement, or null.
 */
export function needsHelenNow(
  r: HelenRequest,
  crossing: HelenCrossing | undefined,
  settlement: HelenSettlement | null,
  linkKnown: boolean,
  now: Date,
): HelenPlace {
  const c = linkKnown ? crossing : undefined;
  const booked = c !== undefined && c.bookingId !== null;
  const live = c !== undefined && c.bookingId !== null && c.status !== 'cancelled';
  const past = isPortalRequestPast(r, now);

  if (r.status === 'declined' || r.status === 'cancelled') {
    if (!linkKnown) return place('needs', 'link-unknown', null, CROSSINGS_UNKNOWN_LINE);
    if (booked && canEndRunHere(c)) {
      return place('needs', '12', 'end-the-run', endTheRunLine(r));
    }
    return place('done', '13');
  }
  if (r.askState === 'open') {
    return { place: 'needs', row: '9', primary: 'accept', gold: r.askKind === 'change', line: null };
  }
  if (r.status !== 'new' && r.status !== 'priced' && r.status !== 'confirmed') return place('done', 'other');
  if (r.status === 'new') return past ? place('done', '2', null, NO_ANSWER_PAST_LINE) : place('needs', '1', 'save-price');
  if (!linkKnown) return place('needs', 'link-unknown', null, CROSSINGS_UNKNOWN_LINE);
  if (r.status === 'confirmed' && live && bookingRequestMismatch(r, c) !== null) return place('needs', '10', 'open-booking');
  if (booked && !live) {
    return place('needs', '6', null, `Booking ${c.jobRef ?? `#${String(c.bookingId)}`} was cancelled. The home still sees this trip.`);
  }
  if (!live && past) return place('done', '4', null, PAST_LINE);
  if (settlement !== null && isSettleableRow(r, c, now)) return place('done', '14', null, settledLine(settlement));
  if (live) return r.status === 'priced' ? place('needs', '5', 'confirm') : place('booking', '11');
  if (c !== undefined) return place('needs', '7', 'confirm');
  return r.status === 'priced' ? place('needs', '3', 'confirm') : place('needs', '8', 'confirm');
}

/** ⭐ Board 2814 M4 (DD-11 row 14) - the Done row's words for a hand settlement. */
export function settledLine(s: Pick<HelenSettlement, 'reason'>): string {
  return s.reason === 'booked_another_way'
    ? 'Settled by hand: booked another way.'
    : 'Settled by hand: not going ahead.';
}

/** DD-11 row 12's words: who ended the request, and what is left to do. The home's
 *  cancel is said as the home's; one made here (whose run was refused) is not. */
function endTheRunLine(r: Pick<HelenRequest, 'status' | 'history'>): string {
  if (r.status === 'declined') return 'Declined. End the run.';
  const byUs = (Array.isArray(r.history) ? r.history : []).some((h) => h.kind === 'cancelled' && h.by === 'ops');
  return byUs ? 'Cancelled. End the run.' : 'The home cancelled. End the run.';
}

/**
 * ⭐ Board 2814 M2 - THE NEEDS YOU NOW LENGTH, the number the rail badge and the
 * `Requests · N` tab label show. Only meaningful on a known link: the caller
 * shows the error mark for a `null` map (see `refreshPortalCount`).
 */
export function countNeedsHelenNow(
  requests: ReadonlyArray<HelenRequest & { requestId: number }>,
  crossings: Readonly<Record<number, HelenCrossing>>,
  now: Date,
  settlements: Readonly<Record<number, HelenSettlement>> = {},
): number {
  return requests.filter(
    (r) => needsHelenNow(r, crossings[r.requestId], settlements[r.requestId] ?? null, true, now).place === 'needs',
  ).length;
}
